import React, { useEffect, useState, useRef, createContext, useContext } from 'react';
import { User, Patient, Encounter, Role, LoginSession, AuditEvent, MaternityFile } from '../types';
import { db } from './firebase';
import { collection, doc, setDoc, updateDoc, deleteDoc, onSnapshot, writeBatch, query } from 'firebase/firestore';
interface DataContextType {
  currentUser: User | null;
  users: User[];
  patients: Patient[];
  encounters: Encounter[];
  loginSessions: LoginSession[];
  auditEvents: AuditEvent[];
  maternityFiles: MaternityFile[];
  login: (fullName: string, password: string) => {
    ok: boolean;
    error?: string;
  };
  logout: () => void;
  addUser: (user: Omit<User, 'id'>) => void;
  updateUser: (id: string, user: Partial<User>) => void;
  deleteUser: (id: string) => void;
  addPatient: (patient: Omit<Patient, 'id'>) => void;
  updatePatient: (id: string, patient: Partial<Patient>) => void;
  deletePatient: (id: string) => void;
  addEncounter: (encounter: Omit<Encounter, 'id'>) => void;
  updateEncounter: (id: string, encounter: Partial<Encounter>) => void;
  getPatientEncounters: (patientId: string) => Encounter[];
  getActiveEncounters: () => Encounter[];
  deleteLoginSessions: () => void;
  deleteLoginSessionsBefore: (date: string) => void;
  deleteLoginSessionsInRange: (start: string, end: string) => void;
  logAudit: (event: Omit<AuditEvent, 'id' | 'at'>) => void;
  addMaternityFile: (file: Omit<MaternityFile, 'id'>) => void;
  updateMaternityFile: (id: string, file: Partial<MaternityFile>) => void;
}
const DataContext = createContext<DataContextType | undefined>(undefined);
const STORAGE_KEY = 'hms_data_v1';
interface StorageData {
  users: User[];
  patients: Patient[];
  encounters: Encounter[];
}
const defaultAdmin: User = {
  id: 'admin-1',
  fullName: 'System Admin',
  password: 'AdminPassword123!',
  role: 'Administrator'
};
// Firestore collection names
const USERS_COL = 'users';
const PATIENTS_COL = 'patients';
const ENCOUNTERS_COL = 'encounters';
const SESSIONS_COL = 'loginSessions';
const AUDIT_COL = 'auditEvents';
const MATERNITY_COL = 'maternityFiles';
const SESSION_ID_KEY = 'hms_session_id';
// Parse navigator.userAgent into a short, readable device label
const parseDevice = (): {
  device: string;
  userAgent: string;
} => {
  if (typeof navigator === 'undefined') {
    return {
      device: 'Unknown device',
      userAgent: ''
    };
  }
  const ua = navigator.userAgent || '';
  // OS detection
  let os = 'Unknown OS';
  if (/Windows NT 10/i.test(ua)) os = 'Windows 10/11';else if (/Windows NT/i.test(ua)) os = 'Windows';else if (/Mac OS X|Macintosh/i.test(ua)) os = 'macOS';else if (/Android/i.test(ua)) os = 'Android';else if (/iPhone|iPad|iPod/i.test(ua)) os = 'iOS';else if (/Linux/i.test(ua)) os = 'Linux';else if (/CrOS/i.test(ua)) os = 'ChromeOS';
  // Browser detection (order matters — Edge before Chrome, etc.)
  let browser = 'Unknown browser';
  if (/Edg\//i.test(ua)) browser = 'Edge';else if (/OPR\/|Opera/i.test(ua)) browser = 'Opera';else if (/Chrome\//i.test(ua) && !/Chromium/i.test(ua)) browser = 'Chrome';else if (/Firefox\//i.test(ua)) browser = 'Firefox';else if (/Safari\//i.test(ua) && !/Chrome/i.test(ua)) browser = 'Safari';
  // Device type
  const isMobile = /Mobi|Android.*Mobile|iPhone|iPod/i.test(ua);
  const isTablet = /iPad|Tablet|Android(?!.*Mobile)/i.test(ua);
  const deviceType = isMobile ? 'Mobile' : isTablet ? 'Tablet' : 'Desktop';
  return {
    device: `${browser} on ${os} · ${deviceType}`,
    userAgent: ua
  };
};
// Resolve approximate location from IP using a free public service.
// Best-effort: if the call fails, the session simply has no location.
const fetchLocation = async (): Promise<{
  location?: string;
  ipAddress?: string;
}> => {
  try {
    const res = await fetch('https://ipapi.co/json/');
    if (!res.ok) return {};
    const data = await res.json();
    const parts = [data.city, data.region, data.country_name].filter(Boolean);
    return {
      location: parts.length > 0 ? parts.join(', ') : undefined,
      ipAddress: data.ip
    };
  } catch (err) {
    console.warn('Location lookup failed:', err);
    return {};
  }
};
export const DataProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [encounters, setEncounters] = useState<Encounter[]>([]);
  const [loginSessions, setLoginSessions] = useState<LoginSession[]>([]);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [maternityFiles, setMaternityFiles] = useState<MaternityFile[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  // Track whether Firestore has delivered initial data
  const firestoreReady = useRef({
    users: false,
    patients: false,
    encounters: false
  });
  // One-shot guard so the duplicate-BMC migration runs at most once per session
  const dedupeRanRef = useRef(false);
  const allFirestoreReady = () => firestoreReady.current.users && firestoreReady.current.patients && firestoreReady.current.encounters;
  // Load from localStorage as initial fallback, then Firestore takes over
  useEffect(() => {
    // Load localStorage cache first for instant display
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed: StorageData = JSON.parse(stored);
        setUsers(parsed.users?.length ? parsed.users : [defaultAdmin]);
        setPatients(parsed.patients || []);
        setEncounters(parsed.encounters || []);
      } catch (e) {
        console.error('Failed to parse stored data', e);
        setUsers([defaultAdmin]);
      }
    } else {
      setUsers([defaultAdmin]);
    }
    // Check session
    const session = sessionStorage.getItem('hms_session');
    if (session) {
      try {
        const user = JSON.parse(session);
        setCurrentUser(user);
      } catch (e) {
        // ignore
      }
    }
    setIsLoaded(true);
    // Set up Firestore real-time listeners
    const unsubUsers = onSnapshot(query(collection(db, USERS_COL)), snapshot => {
      if (!snapshot.empty) {
        const fireUsers = snapshot.docs.map(d => ({
          ...d.data(),
          id: d.id
        }) as User);
        setUsers(fireUsers);
      } else if (!firestoreReady.current.users) {
        // First load and empty — seed default admin to Firestore
        setDoc(doc(db, USERS_COL, defaultAdmin.id), defaultAdmin).catch(console.error);
        setUsers([defaultAdmin]);
      }
      firestoreReady.current.users = true;
    }, error => {
      console.error('Firestore users listener error:', error);
    });
    const unsubPatients = onSnapshot(query(collection(db, PATIENTS_COL)), snapshot => {
      const firePatients = snapshot.docs.map(d => ({
        ...d.data(),
        id: d.id
      }) as Patient);
      setPatients(firePatients);
      firestoreReady.current.patients = true;
    }, error => {
      console.error('Firestore patients listener error:', error);
    });
    const unsubEncounters = onSnapshot(query(collection(db, ENCOUNTERS_COL)), snapshot => {
      const fireEncounters = snapshot.docs.map(d => {
        const data = d.data();
        return {
          ...data,
          id: d.id,
          labRequests: data.labRequests || [],
          radiologyRequests: data.radiologyRequests || [],
          prescriptions: data.prescriptions || []
        } as Encounter;
      });
      setEncounters(fireEncounters);
      firestoreReady.current.encounters = true;
    }, error => {
      console.error('Firestore encounters listener error:', error);
    });
    const unsubSessions = onSnapshot(query(collection(db, SESSIONS_COL)), snapshot => {
      const fireSessions = snapshot.docs.map(d => ({
        ...d.data(),
        id: d.id
      }) as LoginSession);
      setLoginSessions(fireSessions);
    }, error => {
      console.error('Firestore loginSessions listener error:', error);
    });
    const unsubAudit = onSnapshot(collection(db, AUDIT_COL), snapshot => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AuditEvent[];
      setAuditEvents(data);
      localStorage.setItem('bmed_auditEvents', JSON.stringify(data));
    }, error => {
      console.error('Firestore auditEvents listener error:', error);
    });
    const unsubMaternity = onSnapshot(collection(db, MATERNITY_COL), snapshot => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as MaternityFile[];
      setMaternityFiles(data);
      localStorage.setItem('bmed_maternityFiles', JSON.stringify(data));
    }, error => {
      console.error('Firestore maternityFiles listener error:', error);
    });
    return () => {
      unsubUsers();
      unsubPatients();
      unsubEncounters();
      unsubSessions();
      unsubAudit();
      unsubMaternity();
    };
  }, []);
  // Save to localStorage as cache whenever data changes
  useEffect(() => {
    if (!isLoaded) return;
    const data: StorageData = {
      users,
      patients,
      encounters
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [users, patients, encounters, isLoaded]);
  // One-time migration: re-sequence ALL patient file numbers within each
  // registration year so they're contiguous (BMC2025/0001, 0002, 0003 …).
  // Order within a year is by registeredAt ascending (earliest first), with
  // patient.id as a tiebreaker so the result is deterministic across devices.
  // Patient ↔ Encounter ↔ Maternity links use `id`, not `bmcFileNumber`, so
  // renumbering is safe and preserves every relationship.
  //
  // Guarded by a localStorage flag so it runs at most once per browser even
  // across sessions. Bump the flag name (RESEQUENCE_FLAG) to re-trigger.
  const RESEQUENCE_FLAG = 'bmc_resequenced_v1';
  useEffect(() => {
    if (!firestoreReady.current.patients) return;
    if (dedupeRanRef.current) return;
    if (patients.length === 0) return;
    if (localStorage.getItem(RESEQUENCE_FLAG)) {
      dedupeRanRef.current = true;
      return;
    }
    dedupeRanRef.current = true;
    // Group patients by registration year
    const byYear = new Map<number, Patient[]>();
    const currentYear = new Date().getFullYear();
    patients.forEach(p => {
      const year = p.registeredAt ? new Date(p.registeredAt).getFullYear() : currentYear;
      const arr = byYear.get(year) || [];
      arr.push(p);
      byYear.set(year, arr);
    });
    const renumbered: {
      id: string;
      bmcFileNumber: string;
    }[] = [];
    byYear.forEach((group, year) => {
      // Stable ordering: by registeredAt ascending, then by id
      const sorted = [...group].sort((a, b) => {
        const ta = a.registeredAt ? new Date(a.registeredAt).getTime() : 0;
        const tb = b.registeredAt ? new Date(b.registeredAt).getTime() : 0;
        if (ta !== tb) return ta - tb;
        return a.id.localeCompare(b.id);
      });
      const yearPrefix = `BMC${year}/`;
      sorted.forEach((p, idx) => {
        const seq = idx + 1;
        const expected = `${yearPrefix}${seq.toString().padStart(4, '0')}`;
        if (p.bmcFileNumber !== expected) {
          renumbered.push({
            id: p.id,
            bmcFileNumber: expected
          });
        }
      });
    });
    // Mark migration as done regardless of whether anything actually changed,
    // so it doesn't try to run again next session.
    localStorage.setItem(RESEQUENCE_FLAG, new Date().toISOString());
    if (renumbered.length === 0) {
      console.info('[BMC resequence] No changes needed — already in sequence.');
      return;
    }
    console.warn(`[BMC resequence] Updating ${renumbered.length} patient file number(s) to be contiguous per year.`);
    // Apply locally first for instant consistency, then push to Firestore.
    setPatients(prev => prev.map(p => {
      const hit = renumbered.find(r => r.id === p.id);
      return hit ? {
        ...p,
        bmcFileNumber: hit.bmcFileNumber
      } : p;
    }));
    renumbered.forEach(r => {
      updateDoc(doc(db, PATIENTS_COL, r.id), {
        bmcFileNumber: r.bmcFileNumber
      }).catch(err => console.error('Failed to renumber patient', r.id, err));
    });
  }, [patients]);
  const login = (fullName: string, password: string) => {
    const trimmedName = fullName.trim();
    const user = users.find(u => u.fullName.trim().toLowerCase() === trimmedName.toLowerCase());
    if (!user) {
      return {
        ok: false,
        error: 'User not found. Check your full name.'
      };
    }
    if (user.password !== password) {
      return {
        ok: false,
        error: 'Incorrect password.'
      };
    }
    setCurrentUser(user);
    sessionStorage.setItem('hms_session', JSON.stringify(user));
    // Record login session for activity tracking
    const sessionId = `session-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const {
      device,
      userAgent
    } = parseDevice();
    const session: LoginSession = {
      id: sessionId,
      userId: user.id,
      userName: user.fullName,
      role: user.role,
      loginAt: new Date().toISOString(),
      device,
      userAgent,
      location: 'Resolving…'
    };
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    setLoginSessions(prev => [...prev, session]);
    setDoc(doc(db, SESSIONS_COL, sessionId), session).catch(err => console.error('Failed to record login session:', err));
    // Resolve location asynchronously and patch the session when it arrives
    fetchLocation().then(({
      location,
      ipAddress
    }) => {
      const resolved = location || 'Unknown location';
      setLoginSessions(prev => prev.map(s => s.id === sessionId ? {
        ...s,
        location: resolved,
        ipAddress
      } : s));
      updateDoc(doc(db, SESSIONS_COL, sessionId), {
        location: resolved,
        ipAddress: ipAddress || null
      }).catch(err => console.error('Failed to update session location:', err));
    });
    return {
      ok: true
    };
  };
  const logout = () => {
    const activeSessionId = sessionStorage.getItem(SESSION_ID_KEY);
    if (activeSessionId) {
      const logoutAt = new Date().toISOString();
      setLoginSessions(prev => prev.map(s => s.id === activeSessionId ? {
        ...s,
        logoutAt
      } : s));
      updateDoc(doc(db, SESSIONS_COL, activeSessionId), {
        logoutAt
      }).catch(err => console.error('Failed to finalize login session:', err));
      sessionStorage.removeItem(SESSION_ID_KEY);
    }
    setCurrentUser(null);
    sessionStorage.removeItem('hms_session');
  };
  const addUser = (user: Omit<User, 'id'>) => {
    const newUser: User = {
      ...user,
      id: `user-${Date.now()}`
    };
    // Write to Firestore — onSnapshot will update local state
    setDoc(doc(db, USERS_COL, newUser.id), newUser).catch(err => {
      console.error('Failed to add user to Firestore:', err);
      // Fallback: update local state directly
      setUsers(prev => [...prev, newUser]);
    });
  };
  const updateUser = (id: string, data: Partial<User>) => {
    // Optimistic local update
    setUsers(prev => prev.map(u => u.id === id ? {
      ...u,
      ...data
    } : u));
    updateDoc(doc(db, USERS_COL, id), data).catch(err => {
      console.error('Failed to update user in Firestore:', err);
    });
  };
  const deleteUser = (id: string) => {
    setUsers(prev => prev.filter(u => u.id !== id));
    deleteDoc(doc(db, USERS_COL, id)).catch(err => {
      console.error('Failed to delete user from Firestore:', err);
    });
  };
  // Build the next available BMC number for a given year.
  //
  // Strategy:
  //   1. Look at all sequence numbers currently in use for the target year.
  //   2. Walk from 1 upward and pick the FIRST gap (e.g. if 0001, 0002, 0004
  //      are in use, return 0003). This fills holes left by deleted patients
  //      so file numbers stay tight and never collide.
  //   3. If there are no gaps, return max + 1.
  //   4. Final safety: re-check against ALL bmcFileNumbers (any year) and
  //      bump until the candidate is genuinely unique across the database.
  const buildNextBMCNumber = (existingPatients: Patient[], year: number = new Date().getFullYear()): string => {
    const yearPrefix = `BMC${year}/`;
    const usedNumbers = new Set(existingPatients.map(p => p.bmcFileNumber).filter(Boolean));
    const usedSeqsForYear = new Set<number>();
    existingPatients.forEach(p => {
      if (p.bmcFileNumber && p.bmcFileNumber.startsWith(yearPrefix)) {
        const seqStr = p.bmcFileNumber.slice(yearPrefix.length);
        const seq = parseInt(seqStr, 10);
        if (!Number.isNaN(seq)) usedSeqsForYear.add(seq);
      }
    });
    // Find the lowest positive integer not in usedSeqsForYear (gap-filling).
    let nextSeq = 1;
    while (usedSeqsForYear.has(nextSeq)) nextSeq += 1;
    let candidate = `${yearPrefix}${nextSeq.toString().padStart(4, '0')}`;
    // Cross-year safety: ensure global uniqueness against every number on file.
    while (usedNumbers.has(candidate)) {
      nextSeq += 1;
      candidate = `${yearPrefix}${nextSeq.toString().padStart(4, '0')}`;
    }
    return candidate;
  };
  const generateBMCNumber = () => buildNextBMCNumber(patients);
  const addPatient = (patientData: Omit<Patient, 'id'>) => {
    // Compute number against the latest patient list and double-check
    // uniqueness right before save. Bumps if a race created the same number.
    let bmcFileNumber = buildNextBMCNumber(patients);
    const existing = new Set(patients.map(p => p.bmcFileNumber));
    while (existing.has(bmcFileNumber)) {
      // Force the next slot by treating this candidate as already used
      existing.add(bmcFileNumber);
      bmcFileNumber = buildNextBMCNumber([...patients, {
        bmcFileNumber
      } as Patient]);
    }
    const newPatient: Patient = {
      ...patientData,
      id: `pat-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      bmcFileNumber,
      registeredAt: new Date().toISOString()
    };
    // Write to Firestore
    setDoc(doc(db, PATIENTS_COL, newPatient.id), newPatient).catch(err => {
      console.error('Failed to add patient to Firestore:', err);
      setPatients(prev => [...prev, newPatient]);
    });
    // Return immediately for the caller (optimistic)
    setPatients(prev => [...prev, newPatient]);
    return newPatient;
  };
  const updatePatient = (id: string, data: Partial<Patient>) => {
    setPatients(prev => prev.map(p => p.id === id ? {
      ...p,
      ...data
    } : p));
    updateDoc(doc(db, PATIENTS_COL, id), data).catch(err => {
      console.error('Failed to update patient in Firestore:', err);
    });
  };
  const deletePatient = (id: string) => {
    setPatients(prev => prev.filter(p => p.id !== id));
    setEncounters(prev => prev.filter(e => e.patientId !== id));
    // Delete from Firestore
    deleteDoc(doc(db, PATIENTS_COL, id)).catch(err => {
      console.error('Failed to delete patient from Firestore:', err);
    });
    // Also delete associated encounters from Firestore
    encounters.filter(e => e.patientId === id).forEach(e => {
      deleteDoc(doc(db, ENCOUNTERS_COL, e.id)).catch(console.error);
    });
  };
  const createEncounter = (patientId: string) => {
    const newEncounter: Encounter = {
      id: `enc-${Date.now()}`,
      patientId,
      startedAt: new Date().toISOString(),
      stage: 'NURSING',
      pendingLab: false,
      pendingRadiology: false,
      labRequests: [],
      radiologyRequests: [],
      prescriptions: []
    };
    // Optimistic local update
    setEncounters(prev => [...prev, newEncounter]);
    // Write to Firestore
    setDoc(doc(db, ENCOUNTERS_COL, newEncounter.id), newEncounter).catch(err => {
      console.error('Failed to create encounter in Firestore:', err);
    });
    return newEncounter;
  };
  const updateEncounter = (id: string, data: Partial<Encounter>) => {
    // Optimistic local update
    setEncounters(prev => prev.map(e => e.id === id ? {
      ...e,
      ...data
    } : e));
    // Write to Firestore
    updateDoc(doc(db, ENCOUNTERS_COL, id), data as Record<string, any>).catch(err => {
      console.error('Failed to update encounter in Firestore:', err);
    });
  };
  const getPatientEncounters = (patientId: string) => {
    return encounters.filter(e => e.patientId === patientId).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  };
  const getActiveEncounters = () => {
    return encounters.filter(e => e.stage !== 'COMPLETED');
  };
  // ─── Login session deletion ───
  const deleteLoginSessions = () => {
    const ids = loginSessions.map(s => s.id);
    setLoginSessions(prev => prev.filter(s => !ids.includes(s.id)));
    ids.forEach(id => deleteDoc(doc(db, SESSIONS_COL, id)).catch(err => console.error('Failed to delete session:', err)));
  };
  const deleteLoginSessionsBefore = (date: string) => {
    const cutoff = new Date(date).getTime();
    const ids = loginSessions.filter(s => new Date(s.loginAt).getTime() < cutoff).map(s => s.id);
    deleteLoginSessions(ids);
  };
  const deleteLoginSessionsInRange = (start: string, end: string) => {
    const startMs = new Date(start).getTime();
    const endMs = new Date(end).getTime();
    const ids = loginSessions.filter(s => {
      const t = new Date(s.loginAt).getTime();
      return t >= startMs && t <= endMs;
    }).map(s => s.id);
    deleteLoginSessions(ids);
  };
  const logAudit = (event: Omit<AuditEvent, 'id' | 'at'>) => {
    const newEvent: AuditEvent = {
      ...event,
      id: `audit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      at: new Date().toISOString()
    };
    setAuditEvents(prev => [...prev, newEvent]);
    setDoc(doc(db, AUDIT_COL, newEvent.id), newEvent).catch(err => console.error('Failed to log audit event:', err));
  };
  const addMaternityFile = (file: Omit<MaternityFile, 'id'>) => {
    const newFile: MaternityFile = {
      ...file,
      id: `mat-${Date.now()}`
    };
    setMaternityFiles(prev => [...prev, newFile]);
    setDoc(doc(db, MATERNITY_COL, newFile.id), newFile).catch(err => {
      console.error('Failed to add maternity file to Firestore:', err);
    });
  };
  const updateMaternityFile = (id: string, data: Partial<MaternityFile>) => {
    setMaternityFiles(prev => prev.map(m => m.id === id ? {
      ...m,
      ...data
    } : m));
    updateDoc(doc(db, MATERNITY_COL, id), data as Record<string, any>).catch(err => {
      console.error('Failed to update maternity file in Firestore:', err);
    });
  };
  if (!isLoaded) return null;
  return <DataContext.Provider value={{
    currentUser,
    users,
    patients,
    encounters,
    loginSessions,
    auditEvents,
    maternityFiles,
    login,
    logout,
    addUser,
    updateUser,
    deleteUser,
    addPatient,
    updatePatient,
    deletePatient,
    createEncounter,
    updateEncounter,
    getPatientEncounters,
    getActiveEncounters,
    deleteLoginSessions,
    deleteLoginSessionsBefore,
    deleteLoginSessionsInRange,
    logAudit,
    addMaternityFile,
    updateMaternityFile
  }} data-id="element-3840">
      {children}
    </DataContext.Provider>;
};
export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};